/**
 * The story tree (1.I-1 deliverable 20): arcs and parents as roots, the rest under a collapsed
 * "Unassigned"; children load on expand. An ARIA tree with one roving tab stop, its keys from
 * `tree-model.ts`, row actions that stay out of the Tab order, and the open page's row marked,
 * its ancestors opened and scrolled into view.
 */
import type { TreeNodeV1 } from '@codex/contracts';
import { Button, Icon, IconButton, Skeleton, TYPE_KEYS, TypeGlyph, type TypeKey } from '@codex/ui';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { docHref } from '../../editor-services/adapter.js';
import { useCreateChildPage } from '../create.js';
import { useChain, useOpenDocId } from '../header.js';
import { useOpenDoc } from '../open-doc.js';
import { PinDocButton, usePinActions } from '../pins.js';
import { treeKey, treeQueryOptions } from '../queries.js';
import {
  ancestorsToExpand,
  indentPx,
  mergeExpanded,
  showsLevelText,
  splitTopLevel,
  type TreeRow,
  treeKeys,
  UNASSIGNED_LABEL,
  visibleRows,
} from './tree-model.js';

export const TREE_EMPTY = 'No pages yet.';
export const TREE_ERROR = "Couldn't load the tree.";
const EXPANDED_CAP = 200;

function glyphKey(key: string): TypeKey {
  return (TYPE_KEYS as readonly string[]).includes(key) ? (key as TypeKey) : 'note';
}

/**
 * Scrolls the row's nearest scrolling ancestor (the sidebar) just enough to show it, as
 * `scrollIntoView({ block: 'nearest' })` would, without calling it: Chromium moves the
 * sequential focus navigation starting point to an element `scrollIntoView` scrolls to, so a
 * reveal on load sent the next Tab past "Skip to page" to the stop after the row (exit-1a gate,
 * DECISIONS 441). Setting `scrollTop` leaves the starting point where it was.
 */
export function revealRow(row: HTMLElement): void {
  for (let box = row.parentElement; box; box = box.parentElement) {
    const overflow = getComputedStyle(box).overflowY;
    if (overflow !== 'auto' && overflow !== 'scroll') continue;
    if (box.scrollHeight <= box.clientHeight) continue;
    const outer = box.getBoundingClientRect();
    const inner = row.getBoundingClientRect();
    const top = outer.top + box.clientTop;
    const bottom = top + box.clientHeight;
    if (inner.top < top) box.scrollTop -= top - inner.top;
    else if (inner.bottom > bottom)
      box.scrollTop += Math.min(inner.bottom - bottom, inner.top - top);
    return;
  }
}

export function StoryTree({
  expanded,
  onExpandedChange,
}: {
  expanded: readonly string[];
  onExpandedChange: (next: string[]) => void;
}) {
  const client = useQueryClient();
  const open = useOpenDoc();
  const actions = usePinActions();
  const createChild = useCreateChildPage();
  const docId = useOpenDocId();
  const chain = useChain(docId);
  const treeRef = useRef<HTMLDivElement | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);

  const top = useQuery(treeQueryOptions(null));
  const split = useMemo(() => splitTopLevel(top.data ?? []), [top.data]);
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);

  // The levels to load: the expanded rows that are on screen, found through what is cached.
  const wanted: string[] = [];
  const visit = (nodes: readonly TreeNodeV1[]) => {
    for (const node of nodes) {
      if (node.childCount === 0 || !expandedSet.has(node.id)) continue;
      wanted.push(node.id);
      const cached = client.getQueryData<TreeNodeV1[]>(treeKey(node.id));
      if (cached) visit(cached);
    }
  };
  visit(split.roots);
  const levels = useQueries({ queries: wanted.map((id) => treeQueryOptions(id)) });
  const children = new Map<string, readonly TreeNodeV1[]>();
  wanted.forEach((id, index) => {
    const data = levels[index]?.data;
    if (data) children.set(id, data);
  });

  const rows = visibleRows({ top: split, children }, expandedSet);
  const activeKey = rows.some((row) => row.key === focusKey) ? focusKey : (rows[0]?.key ?? null);

  // Open the open page's ancestors once per page.
  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (docId === null || !chain.data || chain.data.length === 0 || !top.data) return;
    if (revealed.current === docId) return;
    // Wait for a top level that already holds the chain's root (it may be stale).
    const rootId = chain.data[0]?.id;
    if (!top.data.some((node) => node.id === rootId)) return;
    revealed.current = docId;
    const add = ancestorsToExpand(
      chain.data.map((crumb) => crumb.id),
      new Set(split.unassigned.map((node) => node.id)),
    );
    if (add.length > 0) onExpandedChange(mergeExpanded(expanded, add, EXPANDED_CAP));
  }, [docId, chain.data, top.data, split.unassigned, expanded, onExpandedChange]);

  // Scroll the open page's row into view when it first appears.
  const scrolled = useRef<string | null>(null);
  const activePresent = docId !== null && rows.some((row) => row.key === docId);
  useEffect(() => {
    if (!activePresent || scrolled.current === docId) return;
    scrolled.current = docId;
    const row = treeRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (row) revealRow(row);
  }, [activePresent, docId]);

  const setOpen = (key: string, on: boolean) => {
    const next = on
      ? mergeExpanded(expanded, [key], EXPANDED_CAP)
      : expanded.filter((id) => id !== key);
    onExpandedChange(next);
  };

  const focusRow = (key: string) => {
    setFocusKey(key);
    treeRef.current?.querySelector<HTMLElement>(`[data-tree-key="${key}"]`)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.getAttribute('role') !== 'treeitem' || event.altKey || event.ctrlKey) return;
    const key = target.getAttribute('data-tree-key');
    const action = treeKeys(rows, key, event.key);
    if (action.type === 'none') return;
    event.preventDefault();
    event.stopPropagation();
    if (action.type === 'focus') focusRow(action.key);
    else if (action.type === 'expand') setOpen(action.key, true);
    else if (action.type === 'collapse') setOpen(action.key, false);
    else open(action.key, event);
  };

  if (top.isPending) return <Skeleton lines={4} variant="rows" label="Loading the story tree" />;
  if (top.isError) {
    return (
      <div className="cx-shell-list-error">
        <p role="alert">{TREE_ERROR}</p>
        <Button size="sm" onClick={() => void top.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <ul className="cx-nav-section__list" data-testid="shell.sidebar.tree">
        <li className="cx-shell-empty">{TREE_EMPTY}</li>
      </ul>
    );
  }

  const byParent = new Map<string | null, TreeRow[]>();
  for (const row of rows) {
    const list = byParent.get(row.parentKey) ?? [];
    list.push(row);
    byParent.set(row.parentKey, list);
  }

  const renderRow = (row: TreeRow) => {
    const node = row.node;
    const title = node?.title ?? UNASSIGNED_LABEL;
    const count = row.kind === 'unassigned' ? split.unassigned.length : null;
    const label = count === null ? title : `${title} (${count})`;
    const kids = byParent.get(row.key) ?? [];
    const current = node !== null && node.id === docId;
    return (
      <div
        key={row.key}
        role="treeitem"
        data-tree-key={row.key}
        aria-level={row.level}
        aria-setsize={row.setSize}
        aria-posinset={row.posInSet}
        aria-expanded={row.expandable ? row.expanded : undefined}
        aria-current={current ? 'page' : undefined}
        aria-label={label}
        tabIndex={row.key === activeKey ? 0 : -1}
        onFocus={(event) => {
          if (event.target === event.currentTarget) setFocusKey(row.key);
        }}
      >
        <div
          className="cx-tree-row"
          data-active={current ? '' : undefined}
          style={{ paddingLeft: indentPx(row.level) + 6 }}
        >
          {row.expandable ? (
            <button
              type="button"
              tabIndex={-1}
              className="cx-tree-twisty"
              aria-label={row.expanded ? `Collapse ${title}` : `Expand ${title}`}
              onClick={() => setOpen(row.key, !row.expanded)}
            >
              <Icon name={row.expanded ? 'caret-down' : 'caret-right'} />
            </button>
          ) : (
            <span className="cx-tree-twisty" aria-hidden="true" />
          )}
          {showsLevelText(row.level) && <span className="cx-tree-level">L{row.level}</span>}
          {node ? (
            <>
              <TypeGlyph type={glyphKey(node.typeKey)} />
              <a
                className="cx-nav-row__label cx-shell-link"
                href={docHref(node.id)}
                title={node.title}
                tabIndex={-1}
                onClick={(event) => open(node.id, event)}
              >
                {node.title}
              </a>
              <span className="cx-tree-actions">
                <PinDocButton docId={node.id} actions={actions} focusable={false} />
                <IconButton
                  icon="plus"
                  label="New page inside"
                  tabIndex={-1}
                  className="cx-row-action"
                  onClick={() => void createChild({ id: node.id, typeKey: node.typeKey })}
                />
              </span>
            </>
          ) : (
            <button
              type="button"
              tabIndex={-1}
              className="cx-tree-unassigned cx-nav-row__label"
              onClick={() => setOpen(row.key, !row.expanded)}
            >
              <Icon name="circle-dashed" />
              <span>{UNASSIGNED_LABEL}</span>
              <span className="cx-nav-row__count">{count}</span>
            </button>
          )}
        </div>
        {row.expanded && kids.length > 0 && (
          // biome-ignore lint/a11y/useSemanticElements: the ARIA tree pattern's group
          <div role="group" className="cx-tree-group">
            {kids.map(renderRow)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={treeRef}
      role="tree"
      aria-label="Story"
      className="cx-tree"
      data-testid="shell.sidebar.tree"
      onKeyDown={onKeyDown}
    >
      {(byParent.get(null) ?? []).map(renderRow)}
    </div>
  );
}
